import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    DetailedHTMLProps,
    FC,
    MouseEvent
} from "react";

import { css, cssMap } from "@compiled/react";

import { isLocalizableHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

type ButtonSize = "large" | "small";
export type ButtonColor = "blue" | "yellow" | "white" | "black" | "light_grey";
export type ButtonColorAgainst = "black" | "other";
type ButtonVariant = "primary" | "secondary";

const getButtonSize = (size: string | undefined): ButtonSize => {
    const buttonSize = size?.toLowerCase();
    if (buttonSize && buttonSize === "small") {
        return "small";
    }
    return "large";
};

const getButtonVariant = (type: string | undefined): ButtonVariant => {
    const buttonType = type?.toLowerCase();
    if (buttonType === "secondary") {
        return "secondary";
    }
    return "primary";
};

const isButtonColor = (color: string): color is ButtonColor => {
    return (
        color === "blue" ||
        color === "yellow" ||
        color === "white" ||
        color === "black" ||
        color === "light_grey"
    );
};

const getButtonColor = (
    color: string | undefined,
    variant: ButtonVariant
): ButtonColor => {
    const buttonColor = color?.toLowerCase();

    if (buttonColor && isButtonColor(buttonColor)) {
        if (variant == "primary") {
            if (
                buttonColor == "blue" ||
                buttonColor == "yellow" ||
                buttonColor == "light_grey"
            )
                return buttonColor;
            else return "blue";
        } else {
            if (
                buttonColor == "black" ||
                buttonColor == "white" ||
                buttonColor == "light_grey"
            )
                return buttonColor;
            else return "black";
        }
    }

    return "blue";
};

const isColorAgainst = (color: string): color is ButtonColorAgainst => {
    return color === "black" || color === "other";
};

const getColorAgainst = (color: string | undefined): ButtonColorAgainst => {
    const colorAgainst = color?.toLowerCase();

    if (colorAgainst && isColorAgainst(colorAgainst)) {
        return colorAgainst;
    }

    return "black";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buttonPrimaryStyles: { [key: string]: any } = cssMap({
    primary_blue: {
        "color": "#ffffff",
        "backgroundColor": "#1868DB",
        "border": "none",

        "&:hover": {
            backgroundColor: "#123263"
        }
    },
    primary_yellow: {
        "color": "#101214",
        "backgroundColor": "#FCA700",
        "border": "none",

        "&:hover": {
            backgroundColor: "#FFE48F"
        }
    },
    primary_light_grey: {
        color: "#101214",
        backgroundColor: "#F8F8F8",
        border: "none"
    },
    secondary_white_against_black: {
        "color": "#ffffff",
        "backgroundColor": "transparent",
        "border": "2px solid #ffffff",
        "&:hover": {
            backgroundColor: "#2B2C2F"
        }
    },
    secondary_white_against_other: {
        "color": "#ffffff",
        "backgroundColor": "transparent",
        "border": "2px solid #ffffff",
        "&:hover": {
            backgroundColor: "#2B2C2F20",
            backgroundBlendMode: "multiply"
        }
    },
    secondary_black: {
        "color": "#101214",
        "backgroundColor": "transparent",
        "border": "2px solid #101214",
        "&:hover": {
            backgroundColor: "#F0F1F2"
        }
    },
    secondary_light_grey: {
        color: "#101214",
        backgroundColor: "#F8F8F8",
        border: "2px solid #101214"
    }
});

const baseStyles = css({
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "inline-flex",
    "fontWeight": 500,
    "fontFamily": "var(--bxp-charlie-text-font-family)",
    "lineHeight": "1.5rem",
    "padding": "10px 24px",
    "textAlign": "center",
    "textDecoration": "none",
    "borderRadius": "40px",

    "&:hover": {
        cursor: "pointer"
    }
});

const fontAndHeightStyles = cssMap({
    large: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem",
        height: "min-content",
        minHeight: "44px",
        minWidth: "120px",
        maxWidth: "368px"
    },
    small: {
        fontSize: "1rem",
        lineHeight: "1.25rem",
        height: "min-content",
        minHeight: "40px",
        minWidth: "100px",
        maxWidth: "268px"
    }
});

export type Adg4ButtonProps = {
    size?: ButtonSize;
    variant?: "primary" | "secondary";
    colorAgainst?: "black" | "other";
    color?: ButtonColor;
    className?: string;
    dataEventProps?: DataEventProps;
} & (
    | DetailedHTMLProps<
          ButtonHTMLAttributes<HTMLButtonElement>,
          HTMLButtonElement
      >
    | DetailedHTMLProps<
          AnchorHTMLAttributes<HTMLAnchorElement>,
          HTMLAnchorElement
      >
);

function isLinkArgs(
    props: unknown
): props is AnchorHTMLAttributes<HTMLAnchorElement> {
    return "href" in (props as AnchorHTMLAttributes<HTMLAnchorElement>);
}

const isSignInUrl = (href: string) => {
    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    return href == signInUrl;
};

const isNonSigninOrWacUrl = (href: string) => {
    return !isLocalizableHref(href) && !isSignInUrl(href);
};

export const Adg4Button: FC<Adg4ButtonProps> = ({
    variant,
    color,
    colorAgainst,
    size,
    className,
    children,
    dataEventProps,
    ...props
}: Adg4ButtonProps) => {
    const isLink = isLinkArgs(props);
    const buttonVariant = getButtonVariant(variant);
    const buttonColor = getButtonColor(color, buttonVariant);
    const buttonColorAgainst = getColorAgainst(colorAgainst);
    const buttonSize: ButtonSize = getButtonSize(size);

    const styles =
        buttonVariant == "secondary" && buttonColor == "white"
            ? `${buttonVariant}_${buttonColor}_against_${buttonColorAgainst}`
            : `${buttonVariant}_${buttonColor}`;

    const variantStyles = buttonPrimaryStyles[styles];
    const fontAndHeightStyle = fontAndHeightStyles[buttonSize];

    const handleOnClick = (
        e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>
    ) => {
        if (props.onClick) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            props.onClick(e as any);
        }
    };

    return isLink ? (
        <a
            className={className}
            css={[baseStyles, variantStyles, fontAndHeightStyle]}
            {...props}
            {...dataEventProps}
            onClick={handleOnClick}
            href={props.href}
            target={
                props.href &&
                /* redirecting to new tab if the href is not on WAC and not a Signin link */
                isNonSigninOrWacUrl(props.href)
                    ? "_blank"
                    : ""
            }
            rel={
                props.href && isNonSigninOrWacUrl(props.href)
                    ? "noreferrer"
                    : ""
            }
        >
            {children}
        </a>
    ) : (
        <button
            className={className}
            css={[baseStyles, variantStyles, fontAndHeightStyle]}
            {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
            {...dataEventProps}
            onClick={handleOnClick}
        >
            {children}
        </button>
    );
};
